<template>
	<div class="review-box-modal__heading-container">
		<RatingBox
			class="review-box-modal__rating-box"
			:rating="rating"
		/>
		<ReviewerInfo
			:review="review"
			size="sm"
		/>
	</div>

	<div class="review-box-list-item__text-content">
		<h4 class="review-box-list-item__title">
			{{ review.Headline }}
		</h4>
		<p class="review-box-list-item__text">
			{{ review.Conclusion }}
		</p>
	</div>

	<dt
		:class="[
			'review-box-modal__recommend',
			review.Recommendation ? 'recommend' : null
		]"
	>
		Weiterempfehlung: {{ recommend }}
	</dt>

	<div
		class="review-box-modal__details-section"
	>
		<template
			v-for="conclusion in conclusions"
			:key="conclusion.name"
		>
			<div class="review-box-modal__details-section--item">
				<CustomerReviewsBar
					:value="conclusion.rating"
					:max-value="6"
					:title="conclusion.name"
					:rating="numberFormat(conclusion.rating)"
					class="review-box-modal__details-section--rating"
				/>
				<dd class="review-box-modal__details-section--review">
					{{ conclusion.conclusion }}
				</dd>
			</div>
		</template>
	</div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import CustomerReviewsBar from '@lmt-rpb/CustomerReviewsBar/CustomerReviewsBar.vue';
import { RATING_MAX_VALUE } from '@global-js/constants';
import ReviewerInfo from './ReviewerInfo.vue';
import type { ReviewType } from '@/interfaces/review-types';
import RatingBox from './RatingBox.vue';
import { numberFormat } from '@/js/utils/numberUtils';

type Conclusion = {
	name: string,
	conclusion: string | null;
	rating: number;
};

interface Props {
    review: ReviewType
}
const props = defineProps<Props>();

const rating = computed(() => `${numberFormat(props.review.RatingOverallValue)}/${RATING_MAX_VALUE}`);

const conclusions = computed((): Conclusion[] => {
	const { Reviews, Conclusion, RatingOverallValue } = props.review;
	const totalConclusion = { name: 'Gesamt', conclusion: Conclusion, rating: RatingOverallValue };
	const reviewConclusions = Object.entries(Reviews)
		.map(([name, { Conclusion: conclusion = '', Value: rating }]) => (
			{ name, conclusion, rating: rating ?? 0 }
		));
	return [totalConclusion, ...reviewConclusions];
});

const recommend = computed((): string => {
	const { Recommendation } = props.review;
	return Recommendation ? 'Ja' : 'Nein';
});

</script>

<style lang="scss" scoped>
.review-box-modal {
	&__heading-container {
		display: flex;
		margin-bottom: 1.6rem;
	}

	&__rating-box {
		margin-right: 1.6rem;
		@include media-query-up($breakpoint-small) {
			margin-right: 4rem;
		}
	}

	&__recommend {
		margin: 1.6rem 0;
		font-weight: $font-weight-semibold;
		background-color: $color-inactive;
		width: max-content;
		padding: 0.8rem;
		border-radius: $border-radius-small;

		@include media-query-up($breakpoint-small) {
			font-size: $font-small-1;
		}

		&.recommend {
			background-color: $color-checkout-highlight;
		}
	}

	&__details-section {
		column-count: 1;
		column-gap: 3.2rem;

		@include media-query-up($breakpoint-small) {
			column-count: 2;
		}

		&--item {
			break-inside: avoid;
			margin-bottom: 2.4rem;
			display: inline-block;
			width: 100%;
		}

		&--rating {
			max-width: 30rem
		}

		&--review {
			margin: 0.8rem 0 0 0;
		}
	}
}

.review-box-list-item {
	&__title {
		margin-bottom: 0.5rem;
		font-weight: $font-weight-semibold;
		font-size: $font-small-1;
		margin: 0 0 0.8rem;
	}

	&__text {
		line-height: 2.3rem;
	}
}
</style>
